import { useTheme } from "@/theme/ThemeProvider";
import { LinearGradient } from "expo-linear-gradient";
import { useFocusEffect } from "expo-router";
import React, { useCallback, useEffect, useState } from "react";
import {
  Platform,
  RefreshControl,
  ScrollView,
  Text,
  View,
  ViewStyle,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { CONTENT_MAX_WIDTH } from "@/constants/layout";
import { getScreenGradientColors } from "@/theme/themes";
import { useFabSetter } from "@/ui/layout/FabSlot";

const SCREEN_GRADIENT_START = { x: 0, y: 0 };
const SCREEN_GRADIENT_END = { x: 1, y: 1 };

type Props = {
  children: React.ReactNode;

  /** Optional: Seitentitel – wird fest oben angezeigt (bei scroll: sticky), ohne Untertitel */
  title?: string;

  /** Aktion rechts in der Titelleiste, z. B. Aktualisieren. Scrollt nicht mit. */
  titleAction?: React.ReactNode;

  /** Standard: true -> SafeArea + Hintergrund */
  safe?: boolean;

  /** Standard: false -> View; true -> ScrollView */
  scroll?: boolean;

  /** Bei scroll=true: optional RefreshControl (z. B. für Pull-to-Refresh) */
  refreshControl?: React.ReactElement<React.ComponentProps<typeof RefreshControl>>;

  /** Extra Padding innen. Standard: theme.space.lg (einheitlicher Seiten-Gutter) */
  padding?: number;

  /** Optional: Content maxWidth (nice für Web/Desktop) */
  maxWidth?: number;

  /** Styles */
  style?: ViewStyle;

  /** Styles fürs Content-Container (bei scroll) */
  contentStyle?: ViewStyle;

  /** Optional: schwebende Hauptaktion (FAB), unten rechts, scrollt nicht mit. */
  fab?: React.ReactNode;

  /** Scroll-Ziel für Seiten, die nach einer Aktion zu einer Karte springen. */
  scrollRef?: React.Ref<ScrollView | null>;

  /** Bleibt unter dem Inhalt stehen und scrollt nicht mit. */
  footer?: React.ReactNode;
};

export function Screen({
  children,
  title,
  titleAction,
  safe = true,
  scroll = false,
  refreshControl,
  padding,
  maxWidth,
  style,
  contentStyle,
  fab,
  scrollRef,
  footer,
}: Props) {
  const { theme } = useTheme();
  const insets = useSafeAreaInsets();
  const pad = padding ?? theme.space.lg;

  // FAB in den globalen Slot (App-Hülle) schreiben, statt im evtl. überlaufenden Screen.
  // Nur während diese Seite fokussiert ist; beim Verlassen wieder leeren.
  const setFab = useFabSetter();
  const [fabFocused, setFabFocused] = useState(false);
  useFocusEffect(
    useCallback(() => {
      setFabFocused(true);
      return () => {
        setFabFocused(false);
        setFab(null);
      };
    }, [setFab]),
  );
  useEffect(() => {
    if (fabFocused) setFab(fab ?? null);
  }, [fabFocused, fab, setFab]);

  const effectiveMaxWidth = maxWidth ?? CONTENT_MAX_WIDTH;
  const gradientColors = getScreenGradientColors(theme);

  // Die Hülle teilt die Höhe: Inhalt oben, Bottom-Nav darunter. minHeight 0
  // lässt den Screen in diesen Rest schrumpfen. 100vh/Fensterhöhe würde die
  // Fußleiste unter die Nav schieben, und overflow:hidden schneidet sie ab.
  // Web: height "100%" nötig, damit flex:1-Kinder nicht auf 0 kollabieren.
  const outerStyle: ViewStyle = {
    flex: 1,
    minHeight: 0,
    ...(Platform.OS === "web" && { height: "100%" as ViewStyle["height"] }),
    ...style,
  };

  const safeAreaPadding: ViewStyle =
    safe && Platform.OS !== "web"
      ? {
          paddingTop: insets.top,
          paddingBottom: 0,
          paddingLeft: insets.left,
          paddingRight: insets.right,
        }
      : {};

  const bottomInset = 0;
  const innerStyle: ViewStyle = {
    flexGrow: 1,
    padding: pad,
    width: "100%",
    maxWidth: effectiveMaxWidth,
    minWidth: 0,
    alignSelf: "center",
    ...(Platform.OS === "web"
      ? ({ boxSizing: "border-box" } as ViewStyle)
      : null),
    ...contentStyle,
  };
  const scrollContentStyle: ViewStyle = {
    ...innerStyle,
    width: "100%",
    maxWidth: effectiveMaxWidth,
    minWidth: 0,
    paddingBottom: pad + bottomInset + theme.space.md,
  };

  const titleBarOuter: ViewStyle = {
    width: "100%",
    backgroundColor: gradientColors[0],
    borderBottomWidth: 1,
    borderBottomColor: theme.color.border,
  };
  const titleBarInner: ViewStyle = {
    maxWidth: effectiveMaxWidth,
    alignSelf: "center" as const,
    width: "100%",
    paddingHorizontal: pad,
    paddingVertical: theme.space.sm,
  };

  const titleNode = title ? (
    <View style={titleBarOuter}>
      <View
        style={[
          titleBarInner,
          {
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: theme.space.sm,
          },
        ]}
      >
        <Text
          style={{
            color: theme.color.text,
            fontSize: theme.fontSize.h2,
            fontWeight: "700",
            letterSpacing: 0.1,
            flex: 1,
            minWidth: 0,
          }}
          numberOfLines={1}
        >
          {title}
        </Text>
        {titleAction}
      </View>
    </View>
  ) : null;

  // Absolut positioniert = reiner Hintergrund, Inhalt liegt immer davor (auch auf Web).
  const gradientFill: ViewStyle = {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  };

  // minHeight: 0 nötig, damit ScrollView in Flex-Layout auf Native/Tablet tatsächlich scrollt (sonst wächst Content und scrollt nicht)
  const scrollViewStyle = {
    flex: 1,
    minHeight: 0,
    width: "100%" as const,
    maxWidth: "100%" as const,
  };

  const footerNode = footer ? (
    <View
      testID="screen-footer"
      style={{
        width: "100%",
        maxWidth: effectiveMaxWidth,
        alignSelf: "center",
        flexShrink: 0,
        paddingHorizontal: pad,
        paddingTop: theme.space.sm,
        paddingBottom: pad,
        backgroundColor: theme.color.bg,
        borderTopWidth: 1,
        borderTopColor: theme.color.border,
      }}
    >
      {footer}
    </View>
  ) : null;

  const contentLayer = (
    <View style={[{ flex: 1, minHeight: 0 }, safeAreaPadding]}>
      {scroll && title ? (
        <>
          {titleNode}
          <ScrollView
            ref={scrollRef}
            style={scrollViewStyle}
            contentContainerStyle={scrollContentStyle}
            keyboardShouldPersistTaps="handled"
            refreshControl={refreshControl}
          >
            {children}
          </ScrollView>
          {footerNode}
        </>
      ) : scroll ? (
        <>
          <ScrollView
            ref={scrollRef}
            style={scrollViewStyle}
            contentContainerStyle={scrollContentStyle}
            keyboardShouldPersistTaps="handled"
            refreshControl={refreshControl}
          >
            {children}
          </ScrollView>
          {footerNode}
        </>
      ) : title ? (
        <>
          {titleNode}
          <View style={[innerStyle, { flex: 1, minHeight: 0 }]}>{children}</View>
        </>
      ) : (
        <View style={[innerStyle, { flex: 1, minHeight: 0 }]}>{children}</View>
      )}
    </View>
  );

  return (
    <View style={outerStyle}>
      <LinearGradient
        colors={gradientColors}
        start={SCREEN_GRADIENT_START}
        end={SCREEN_GRADIENT_END}
        style={gradientFill}
      />
      {contentLayer}
    </View>
  );
}
